import { weenatIntl as wintl } from '@weenat/wintl';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import omit from 'lodash-es/omit';
import uniq from 'lodash-es/uniq';
import moment from 'moment-timezone';
import { useClient } from '../../ClientProvider';
import TimeSteps from '../../enums/TimeSteps';
import { allDssSources, allWeatherSources, availableOperationPerSource, getPredicateKey, isAppleScabParams, isBased, isCumul, isDssSource, isForecasted, isLookupOp, isThresholded, isWaterStatusThresholded, sourceToMetric, targetKinds } from '../../resources/alerts.types';
import { isCumulativeSumMetric, toFrontEndAccumulationMetric } from '../../resources/cumulativeSum';
import useQuery from '../../useQuery';
import { getConverters, getMyUnit } from '../units';
import { isDevice, isPlot, isStation } from '../utils/resourcesTypeCheckers';
/**
 * ALERT TUNNEL NAVIGATION
 */
/** List of steps sorted in the right order for the creation of a simple alert */
export const predicateCreationSteps = [
    'pickPredicateSource',
    'pickPredicateOperation',
    'setupPredicate'
];
/** List of steps sorted in the right order for the creation of a simple alert */
export const simpleAlertCreationSteps = [
    'pickAlertCreationType',
    ...predicateCreationSteps,
    'pickTargets',
    'validation'
];
/** List of steps sorted in the right order for the creation of a multi events/predicates alert */
export const multiParamsAlertCreationSteps = [
    'pickAlertCreationType',
    ...predicateCreationSteps,
    'validateMultiplePredicates',
    'pickTargets',
    'validation'
];
/** List of steps without predicate breadcrumbs */
export const stepsWithoutBreadcrumbs = [
    'pickAlertCreationType',
    'validation',
    'validateMultiplePredicates'
];
export const addNotificationMethodStep = (currentSteps) => {
    const listLength = currentSteps.length;
    const newSteps = currentSteps;
    if (!newSteps.includes('pickNotificationMethod'))
        newSteps.splice(listLength - 1, 0, 'pickNotificationMethod');
    return newSteps;
};
export const removeParamSetupStep = (currentSteps) => {
    let newSteps = currentSteps;
    if (currentSteps.includes('setupPredicate'))
        newSteps = currentSteps.filter((s) => s !== 'setupPredicate');
    return newSteps;
};
export const operationsWithoutParams = ['change'];
export const isOperationWithoutParams = (op) => {
    return operationsWithoutParams.includes(op);
};
export const resetToDefaultSteps = (creationType) => creationType === 'simple' ? simpleAlertCreationSteps : multiParamsAlertCreationSteps;
/**
 * UTILS
 */
/** Alert with default fields */
export const defaultAlert = {
    name: '',
    isVoiceCallEnabled: false,
    isActive: true,
    predicates: {},
    targets: {
        plots: [],
        devices: [],
        stations: []
    },
    deviceIdPerStationId: {}
};
/**
 * Get the label associated to the given source
 */
export const getDssLabelPerSource = (src) => {
    switch (src) {
        case 'IRRI_TENSIO':
            return wintl.t('dss.irritensio.dss.name');
        case 'IRRI_CAPA':
            return wintl.t('dss.capacitiveIrrigation.dss.name');
        case 'GDD':
            return wintl.t('metrics.heatAccumulation', { capitalize: true });
        case 'TAVMILLS':
            return wintl.t('alerts.apple_scab_risk', { capitalize: true });
    }
};
export const useGetThresholdFromParams = (params) => {
    const client = useClient();
    const horizons = useQuery(client.horizons.getAllPage()).data || [];
    let paramValue = undefined;
    if (!isNil(params)) {
        if (isCumul(params)) {
            paramValue = params.threshold;
        }
        else if (isThresholded(params)) {
            paramValue = params.threshold;
        }
        else if (isForecasted(params)) {
            paramValue = params.amountInHours;
        }
        else if (isWaterStatusThresholded(params)) {
            paramValue = {
                horizons: horizons
                    .filter((h) => params.horizons.includes(h.id))
                    .map((hrz) => `${hrz.value} cm`)
                    .join(', '),
                thresholds: params.thresholds
                    .map((thr) => wintl.t(`dss.irritensio.flags.${thr}`, { capitalize: true }))
                    .join(', ')
            };
        }
        else if (isAppleScabParams(params)) {
            paramValue = {
                infectionLevels: params.infectionLevels
                    .map((lvl) => wintl.t(`dss.tavmills.infectionLevel.${lvl}`, { capitalize: true }))
                    .join(', ')
            };
        }
    }
    return paramValue;
};
export const getAlertDefaultNameKey = (alertType, predicateKey) => {
    let defaultKey = '';
    if (alertType === 'multiParams') {
        defaultKey = 'alerts.types.multiParams.name';
    }
    else if (predicateKey) {
        defaultKey = `alerts.predicateKeys.${predicateKey}.defaultAlertName`;
        // to avoid declaring the same translation twice
        if (predicateKey.includes('lt')) {
            defaultKey = `alerts.predicateKeys.${predicateKey.replace('lt-', 'gt-')}.defaultAlertName`;
        }
        if (predicateKey.includes('T_CAL')) {
            defaultKey = `alerts.predicateKeys.${predicateKey.replace('T_CAL', 'T_SOIL')}.defaultAlertName`;
        }
    }
    return defaultKey;
};
export const toAlertPayload = (alert) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _id, userId, statuses, updatedAt, currentlyTriggerCount, ...alertPayload } = alert;
    return alertPayload;
};
/**
 * Transforms an @param alert into an AlertPayload
 */
export const computeInitialAcc = async (alert, targetsWithTZ, client) => {
    const predicatesWithInitialAcc = await Object.entries(alert.predicates).reduce(async (accPromise, [key, predicate]) => {
        const acc = await accPromise;
        const castedKey = key;
        const params = predicate.params;
        if (isCumul(params)) {
            const base = isBased(params) ? params.base : undefined;
            const metric = sourceToMetric(predicate.source);
            const initialAcc = { plots: {}, devices: {}, stations: {} };
            const since = params.since;
            for (const targetKind of targetKinds) {
                const currentCumulValues = await Promise.all(alert.targets[targetKind].map(async (id) => {
                    const targetWithTZ = targetsWithTZ[targetKind].find((target) => target.id === id);
                    if (!targetWithTZ) {
                        throw new Error('targetWithTZ not found');
                    }
                    const startOfUnit = predicate.source === 'GDD' ? 'day' : 'hour';
                    const startOfSince = since
                        ? moment.unix(since).tz(targetWithTZ.timezone).startOf(startOfUnit).unix()
                        : undefined;
                    const startOfNow = moment().tz(targetWithTZ.timezone).startOf(startOfUnit).unix();
                    if (startOfSince !== startOfNow && isCumulativeSumMetric(metric)) {
                        const request = await client.cumulativeSum
                            .result({
                            plotId: targetKind === 'plots' ? id : undefined,
                            deviceId: targetKind === 'devices' ? id : undefined,
                            stationId: targetKind === 'stations' ? id : undefined,
                            base,
                            since: since ? `${startOfSince}` : undefined,
                            before: startOfNow ? `${startOfNow}` : undefined,
                            metrics: [toFrontEndAccumulationMetric(metric)],
                            span: predicate.source === 'GDD' ? TimeSteps.perDays : TimeSteps.perHours
                        })
                            .request();
                        return { id, value: request.sums[metric] || 0 };
                    }
                    else {
                        return { id, value: 0 };
                    }
                }));
                currentCumulValues.forEach((plotValue) => (initialAcc[targetKind][plotValue.id] = plotValue.value));
            }
            acc[castedKey] = {
                ...predicate,
                params: {
                    /** We omit hour since it only is a necessity in the form schema https://gitlab.com/weenat-front/client/-/blob/master/src/schemas.ts#L682, but this is encoded in the {@link CumulParams.since} field */
                    ...omit(params, 'hour'),
                    initialAcc
                }
            };
        }
        else {
            acc[castedKey] = predicate;
        }
        return acc;
    }, Promise.resolve({}));
    let deviceIdPerStationId = {};
    if (alert.targets.stations.length > 0) {
        const stations = await client.stations.getAll({ ids: alert.targets.stations }).request();
        deviceIdPerStationId = stations.reduce((acc, station) => {
            acc[station.id] = station.deviceId;
            return acc;
        }, deviceIdPerStationId);
    }
    return {
        ...defaultAlert,
        ...alert,
        deviceIdPerStationId,
        predicates: predicatesWithInitialAcc
    };
};
/**
 * Checks if an alert contains frost metrics within its @param alertPredicates.
 *
 * If there is one predicate, check if its source is T_WET || T_DRY
 *
 * If there is two predicate it must be with both T_WET && T_DRY
 *
 * Otherwise this is not a frost alert eligible to voice call enabling
 */
export const isFrostAlert = (alertPredicates) => {
    const currentPredicatesSources = (Array.isArray(alertPredicates) ? alertPredicates : Object.values(alertPredicates)).map((pdc) => pdc.source);
    if (currentPredicatesSources.length === 2) {
        return currentPredicatesSources.includes('T_WET') && currentPredicatesSources.includes('T_DRY');
    }
    else if (currentPredicatesSources.length === 1) {
        return currentPredicatesSources.includes('T_WET') || currentPredicatesSources.includes('T_DRY');
    }
    else {
        return false;
    }
};
export const getMessagePerRainIntensityRange = (rainIntensity) => {
    let msg = '';
    if (rainIntensity <= 3) {
        msg = wintl.t('alerts.rain_intensity_bearings.continuous_light_rain');
    }
    else if (rainIntensity <= 7) {
        msg = wintl.t('alerts.rain_intensity_bearings.moderate_rain');
    }
    else {
        msg = wintl.t('alerts.rain_intensity_bearings.heavy_rain');
    }
    return msg;
};
export const getPredicateMetric = (predicate) => {
    const { source } = predicate;
    const key = getPredicateKey(predicate);
    const metric = source === 'GDD'
        ? 'heatAccumulation'
        : key === 'sliding-cumul-gt-RR'
            ? 'rainfallIntensity'
            : sourceToMetric(source);
    return metric;
};
export const getPredicateUnit = (predicate) => {
    const metric = getPredicateMetric(predicate);
    return getMyUnit(metric);
};
/**
 * Converts any @type Alert["targets"] into @type TargetsWithTZ
 * WARN : This will replace the given targets, if a target isn't inside the complete targets array it will be removed
 */
export const convertTargetsToTargetsWithTz = ({ targetsToConvert, completeTargets }) => Object.entries(targetsToConvert).reduce((acc, [targetKind, ids]) => {
    const castedTargetKind = targetKind;
    ids.forEach((id) => {
        const selector = castedTargetKind === 'plots'
            ? isPlot
            : castedTargetKind === 'devices'
                ? isDevice
                : isStation;
        // Not pushing or checking anything if there is no complete targets available
        if (!isEmpty(completeTargets)) {
            const completeTarget = completeTargets.find((target) => {
                return target.id === id && selector(target);
            });
            if (completeTarget) {
                acc[castedTargetKind].push({ id, timezone: completeTarget.timezone });
            }
        }
    });
    return acc;
}, { devices: [], plots: [], stations: [] });
export const convertTargetsWithTzToTargets = (targetsToConvert) => ({
    plots: targetsToConvert.plots.map((ptz) => ptz.id),
    devices: targetsToConvert.devices.map((dtz) => dtz.id),
    stations: targetsToConvert.stations.map((stz) => stz.id)
});
/** Extracts predicates from an alert */
export const getAlertPredicates = (alert) => {
    return Object.values(alert.predicates);
};
/** Extracts sources from an alert */
export const getAlertSources = (alert) => {
    return getAlertPredicates(alert).map((p) => p.source);
};
const metricsToSource = (metrics) => {
    const result = [];
    metrics.forEach((m) => {
        if (m === 'HPOT') {
            result.push('IRRI_TENSIO');
        }
        else if (m === 'U_CAPA') {
            result.push('IRRI_CAPA');
        }
        else if (m === 'T') {
            result.push('GDD', 'T');
        }
        else {
            result.push(m);
        }
    });
    return result;
};
export const getDisabledSources = ({ predicates, initialTarget }) => {
    let disabledSources = predicates
        .map(({ source }) => source)
        .filter((x) => x != undefined);
    // Isolating all sources that aren't part of the targets available metrics
    if (initialTarget) {
        const allSourcesAsMetrics = [...allWeatherSources, ...allDssSources].map((s) => sourceToMetric(s));
        const additionalDisabledSources = allSourcesAsMetrics.filter((m) => !initialTarget.availableMeasures.includes(m));
        disabledSources = uniq([...disabledSources, ...metricsToSource(additionalDisabledSources)]);
    }
    return disabledSources;
};
/**
 * ALERT FORM VALIDATIONS
 */
function throwBadOperation(op) {
    throw new Error('Unknown operation kind: ' + op);
}
export const getSchemaNamePerOperation = (op, src) => {
    let schemaName = undefined;
    switch (op) {
        case 'lt':
        case 'gt':
            if (src === 'T_DRY' || src === 'T_WET') {
                schemaName = 'setupFrostThresholdParams';
            }
            else if (src === 'T' || src === 'T_SOIL') {
                schemaName = 'setupTemperatureThresholdParams';
            }
            else if (src === 'T_CAPA') {
                schemaName = 'setupSoilTemperatureWithHorizonParams';
            }
            else if (src === 'U') {
                schemaName = 'setupHumidityThresholdParams';
            }
            else if (src === 'FF' || src === 'FXY') {
                schemaName = 'setupWindThresholdParams';
            }
            else if (src === 'LW_V') {
                schemaName = 'setupLeafWetnessThresholdParams';
            }
            else {
                schemaName = 'setupThreshold';
            }
            break;
        case 'cumul-gt':
            if (src === 'RR') {
                schemaName = 'setupRainCumulParams';
            }
            else if (src === 'LW_D') {
                schemaName = 'setupLeafWetnessDurationParams';
            }
            else if (src === 'GDD') {
                schemaName = 'setupGddCumulParams';
            }
            break;
        case 'near-gt':
            schemaName = 'setupForecastedParams';
            break;
        case 'sliding-cumul-gt':
            schemaName = 'setupRainIntensityParams';
            break;
        // For peculiar cases
        case 'eq':
            if (src === 'IRRI_CAPA' || src === 'IRRI_TENSIO') {
                schemaName = 'setupWaterStatusParams';
            }
            else if (src == 'TAVMILLS') {
                schemaName = 'setupAppleScabInfectionChangeParams';
            }
            break;
        // No params for a change operation
        case 'change':
            schemaName = undefined;
            break;
        default:
            throwBadOperation(op);
    }
    return schemaName;
};
/** List of available hours spans before receiving an alert on forecast */
export const availableAmountsOfHours = [1, 3, 6, 12, 24];
/** List of available intervals for rain intensity */
export const availableRainIntensityThresholds = [1, 4, 8];
const thresholdInitialValue = (converter, unit) => ({
    threshold: converter(0, unit)
});
const cumulInitialValues = (converter, unit) => ({
    threshold: converter(1, unit),
    since: new Date(Date.now()),
    isRepeatable: false
});
export const schemasInitialValues = (metric, unit) => {
    const converter = getConverters(metric);
    return {
        setupThreshold: thresholdInitialValue(converter, unit),
        setupSoilTemperatureThresholdParams: thresholdInitialValue(converter, unit),
        setupSoilTemperatureWithHorizonParams: {
            ...thresholdInitialValue(converter, unit),
            horizon: undefined
        },
        setupTemperatureThresholdParams: thresholdInitialValue(converter, unit),
        setupFrostThresholdParams: thresholdInitialValue(converter, unit),
        setupWindThresholdParams: thresholdInitialValue(converter, unit),
        setupHumidityThresholdParams: thresholdInitialValue(converter, unit),
        setupLeafWetnessThresholdParams: { threshold: 0 },
        setupRainCumulParams: {
            ...cumulInitialValues(converter, unit),
            threshold: 1
        },
        setupGddCumulParams: {
            ...cumulInitialValues(converter, unit),
            threshold: 10,
            base: 6
        },
        setupLeafWetnessDurationParams: {
            ...cumulInitialValues(converter, unit)
        },
        setupWaterStatusParams: {
            horizons: [],
            thresholds: []
        },
        setupForecastedParams: { amountInHours: availableAmountsOfHours[0] },
        setupRainIntensityParams: thresholdInitialValue(converter, unit),
        setupAppleScabInfectionChangeParams: { infectionLevels: [] }
    };
};
/**
 * @returns record of schemas name associated to settings (converted)
 */
export const getConvertedAlertFieldSettings = ({ metric, convertNumberValue }) => {
    const convert = (value) => convertNumberValue({ metric, value });
    return {
        setupSoilTemperatureThresholdParams: {
            threshold: {
                min: convert(0),
                max: convert(30)
            }
        },
        setupSoilTemperatureWithHorizonParams: {
            threshold: {
                min: convert(0),
                max: convert(30)
            }
        },
        setupTemperatureThresholdParams: {
            threshold: {
                min: convert(-20),
                max: convert(60)
            }
        },
        setupFrostThresholdParams: {
            threshold: {
                min: convert(-20),
                max: convert(60)
            }
        },
        setupWindThresholdParams: {
            threshold: {
                min: convert(0),
                max: convert(100)
            }
        },
        setupHumidityThresholdParams: {
            threshold: {
                min: 0,
                max: 100
            }
        },
        setupLeafWetnessDurationParams: {
            threshold: {
                min: 1,
                max: 72
            }
        },
        setupLeafWetnessThresholdParams: {
            threshold: {
                min: 0,
                max: 10
            }
        },
        setupRainIntensityParams: {
            threshold: {
                min: convert(0),
                max: convert(10)
            }
        },
        setupGddCumulParams: {
            base: {
                min: 0,
                max: 20
            },
            threshold: {
                min: convert(10),
                max: convert(100000)
            }
        },
        setupRainCumulParams: {
            threshold: {
                min: convert(1),
                max: convert(100000)
            }
        }
    };
};
export const getFixedSource = (currentPredicate, op) => currentPredicate.source === 'LW_V' && op === 'cumul-gt'
    ? 'LW_D'
    : currentPredicate.source === 'LW_D' && op !== 'cumul-gt'
        ? 'LW_V'
        : currentPredicate.source;
const isCompatibleOperation = (currentPredicate, newOperation) => currentPredicate.op === newOperation ||
    (currentPredicate.source !== 'IRRI_CAPA' &&
        currentPredicate.source !== 'IRRI_TENSIO' &&
        isLookupOp(currentPredicate.op) &&
        isLookupOp(newOperation));
export const buildPredicateWithNewOperation = (currentPredicate, op) => {
    const fixedSource = getFixedSource(currentPredicate, op);
    return isOperationWithoutParams(op)
        ? {
            ...currentPredicate,
            op,
            source: fixedSource,
            params: {}
        }
        : {
            ...currentPredicate,
            op,
            params: isCompatibleOperation(currentPredicate, op) ? currentPredicate.params : {},
            source: fixedSource
        };
};
export const getAvailableOperationsAsOptions = (source, currentAlertCreationType) => {
    const getOpPerSource = (aSource) => (aSource === 'RR'
        ? availableOperationPerSource[aSource].filter((op) => 
        // Only allow near-gt (forecasts) op on single predicate alerts
        op === 'near-gt' ? currentAlertCreationType === 'simple' : true)
        : availableOperationPerSource[aSource]).map((op) => ({
        value: op,
        label: 
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        wintl.t(`alerts.predicateKeys.${op}-${aSource}.label`) ||
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            wintl.t(`alerts.operators.${op}.label`),
        description: 
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        wintl.t(`alerts.predicateKeys.${op}-${aSource}.description`) ||
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            wintl.t(`alerts.operators.${op}.description`)
    }));
    const opPerSource = getOpPerSource(source);
    return source === 'LW_V' || source === 'LW_D'
        ? [...getOpPerSource('LW_V'), ...getOpPerSource('LW_D')]
        : opPerSource;
};
export const getSourceLabel = (source) => isDssSource(source)
    ? getDssLabelPerSource(source)
    : source !== 'LW_D'
        ? wintl.t(`metrics.${source}`, { capitalize: true })
        : wintl.t(`metrics.LW_V`, { capitalize: true });
export const getTargetTriggerStatus = (target, statuses) => {
    const targetStatuses = isPlot(target)
        ? statuses?.plots[target.id]
        : isDevice(target)
            ? statuses?.devices[target.id]
            : statuses?.stations[target.id];
    const targetPredicates = !isNil(targetStatuses) ? Object.entries(targetStatuses) : [];
    return !isEmpty(targetPredicates) && targetPredicates.every((s) => s[1].inProgress);
};
